<template>
    <div>
        <div class="content__inner content__inner--sm" v-if="!loading && cropType">
            <header class="content__title pl-0">
                <h1>Add a {{ cropType.name }} new block</h1>
            </header>

            <div class="card">
                <div class="card-header">
                    <h2 class="card-title">Block Details</h2>
                </div>

                <div class="card-body">
                    <form @submit.prevent="attemptCreate" ref="createForm" novalidate>
                        <div class="form-group row">
                            <label for="nameInput" class="col-sm-2 col-form-label">Block Name</label>
                            <div class="col-sm-10">
                                <div class="position-relative">
                                    <input type="text" id="nameInput" name="name" v-validate="validation.name" v-model="name"
                                           placeholder="Block Name" class="form-control" required>
                                    <div class="invalid-feedback" v-if="errors.has('name')">{{ errors.first('name') }}</div>
                                    <i class="form-group__bar"></i>
                                </div>
                            </div>
                        </div>

                        <div class="form-group row">
                            <label class="col-sm-2 col-form-label">Block District</label>

                            <div class="col-sm-10">
                                <div class="position-relative">
                                    <VueSelect
                                            v-validate="{required: true}"
                                            data-vv-name="district"
                                            v-model="district"
                                            :options="districts"
                                            :allow-empty="false"
                                            deselect-label=""
                                            placeholder="District"
                                            label="name"
                                            track-by="id"
                                    >
                                    </VueSelect>
                                    <div class="invalid-feedback" v-if="errors.has('district')">{{ errors.first('district') }}</div>
                                    <i class="form-group__bar"></i>
                                </div>
                            </div>
                        </div>

                        <div class="form-group row">
                            <label class="col-sm-2 col-form-label">Block Crop Type</label>
                            <div class="col-sm-10">
                                <input type="text" :value="cropType.name" class="form-control form-control-static" readonly>
                                <i class="form-group__bar"></i>
                            </div>
                        </div>

                        <div class="form-group row">
                            <label class="col-sm-2 col-form-label">Block Variety</label>
                            <div class="col-sm-10">
                                <div class="position-relative">
                                    <VueSelect
                                            v-validate="validation.variety"
                                            data-vv-name="variety"
                                            v-model="variety"
                                            :options="cropType.varietyGroups"
                                            :allow-empty="false"
                                            deselect-label=""
                                            placeholder="Block Variety"
                                            group-values="varieties"
                                            group-label="name"
                                            label="name"
                                            track-by="id"
                                    >
                                    </VueSelect>
                                    <div class="invalid-feedback" v-if="errors.has('variety')">{{ errors.first('variety') }}</div>
                                    <i class="form-group__bar"></i>
                                </div>
                            </div>
                        </div>

                        <div class="form-group row">
                            <label class="col-sm-2 col-form-label">Year Planted</label>
                            <div class="col-sm-10">
                                <div class="position-relative">
                                    <VueMask v-model.number="year_planted" class="form-control"
                                             :class="{'is-invalid': errors.has('year_planted')}"
                                             placeholder="0..."
                                             v-validate="validation.year_planted"
                                             data-vv-name="year_planted"
                                             data-vv-as="year planted"
                                             :options="{date: true, datePattern: ['Y'], stripLeadingZeroes: true}" />
                                    <div class="invalid-feedback" v-if="errors.has('year_planted')">{{ errors.first('year_planted') }}</div>
                                    <i class="form-group__bar"></i>
                                </div>
                            </div>
                        </div>

                        <div class="form-group row">
                            <label class="col-sm-2 col-form-label">Total Area</label>
                            <div class="col-sm-10">
                                <div class="position-relative">
                                    <VueMask v-model.number="area" class="form-control"
                                             :class="{'is-invalid': errors.has('area')}"
                                             placeholder="0 hectares"
                                             v-validate="validation.area"
                                             data-vv-name="area"
                                             data-vv-as="area"
                                             :options="{numeral: true, numeralPositiveOnly: true}" />
                                    <div class="invalid-feedback" v-if="errors.has('area')">{{ errors.first('area') }}</div>
                                    <i class="form-group__bar"></i>
                                </div>
                            </div>
                        </div>

                        <div class="form-group row">
                            <label class="col-sm-2 col-form-label">Tree Number</label>
                            <div class="col-sm-10">
                                <div class="position-relative">
                                    <VueMask v-model.number="num_trees" class="form-control"
                                             :class="{'is-invalid': errors.has('num_trees')}"
                                             placeholder="0 trees"
                                             :options="{numeral: true, numeralPositiveOnly: true, numeralDecimalScale: 0}" />
                                    <i class="form-group__bar"></i>
                                </div>
                            </div>
                        </div>

                        <div class="form-group row">
                            <label class="col-sm-2 col-form-label">Tree Spacing</label>
                            <div class="col-sm-10">
                                <div class="position-relative">
                                    <VueMask v-model.number="tree_spacing" class="form-control"
                                             :class="{'is-invalid': errors.has('tree_spacing')}"
                                             placeholder="0 meters"
                                             :options="{numeral: true, numeralPositiveOnly: true}" />
                                    <i class="form-group__bar"></i>
                                </div>
                            </div>
                        </div>

                        <div class="form-group row">
                            <label class="col-sm-2 col-form-label">Row Spacing</label>
                            <div class="col-sm-10">
                                <div class="position-relative input-group">
                                    <VueMask v-model.number="row_spacing" class="form-control"
                                             :class="{'is-invalid': errors.has('row_spacing')}"
                                             placeholder="0 meters"
                                             :options="{numeral: true, numeralPositiveOnly: true}" />
                                    <i class="form-group__bar"></i>
                                </div>
                            </div>
                        </div>

                        <div class="form-group row">
                            <label class="col-sm-2 col-form-label">Canopy Area</label>
                            <div class="col-sm-10">
                                <div class="position-relative">
                                    <VueMask :value="canopyArea" class="form-control" readonly placeholder="0 hectares"
                                             :options="{numeral: true, numeralPositiveOnly: true, numeralDecimalScale: 3}" />
                                    <i class="form-group__bar"></i>
                                </div>
                            </div>
                        </div>

                        <div class="text-right">
                            <button type="submit" class="btn btn-success">Create</button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
        <div class="content__inner" v-if="!loading && !cropType">
            <div class="alert alert-danger text-center">
                <h4 class="alert-heading m-0">
                    <strong>Whoops!</strong> It looks like you have not enabled this crop type.
                </h4>
            </div>
        </div>
    </div>
</template>

<script>
import createBlockMutation from '@views/Blocks/Create/mutationCreateBlock.graphql';
import currentUserQuery from '@views/Blocks/Create/queryCurrentUser.graphql';
import findCropTypeQuery from '@views/Blocks/Create/queryfindGrowerLinkedCropType.graphql';
import listBlocksQuery from '@views/Blocks/Create/queryListBlocks.graphql';

export default {
  name: 'CreateBlock',
  props: ['cropTypeId'],
  apollo: {
    currentUser: {
      query: currentUserQuery,
      update: data => data.currentUser,
    },
    cropType: {
      query: findCropTypeQuery,
      update: data => data.findGrowerCropTypeLink,
      variables() {
        return {
          input: {
            id: this.cropTypeId,
          },
        };
      },
    },
  },
  data() {
    return {
      loading: 0,
      currentUser: null,
      cropType: null,
      name: '',
      variety: null,
      year_planted: null,
      area: null,
      num_trees: null,
      row_spacing: null,
      tree_spacing: null,
      district: null,
      validation: {
        name: { required: true },
        variety: { required: true },
      },
    };
  },
  computed: {
    districts() {
      return this.currentUser.region.districts;
    },
    canopyArea() {
      return this.row_spacing * this.tree_spacing * this.num_trees / 10000;
    },
  },
  methods: {
    attemptCreate() {
      this.errors.remove('global');
      this.$validator.validateAll().then(validationWasSuccessful => {
        if (!validationWasSuccessful) {
          return;
        }

        this.$apollo
          .mutate({
            mutation: createBlockMutation,
            variables: {
              input: {
                name: this.name,
                variety_id: this.variety.id,
                district_id: this.district.id,
                year_planted: this.year_planted,
                area: this.area,
                num_trees: this.num_trees,
                row_spacing: this.row_spacing,
                tree_spacing: this.tree_spacing,
              },
            },
            update: (store, { data: { createBlock: createdBlock } }) => {
              try {
                const data = store.readQuery({ query: listBlocksQuery });
                data.filterBlocks.push(createdBlock);
                store.writeQuery({ query: listBlocksQuery, data });
              } catch (err) {
                console.warn('filterBlocks probably wasn\'t loaded');
              }
            },
            optimisticResponse: {
              __typename: 'Mutation',
              createBlock: {
                __typename: 'Block',
                id: -1,
                name: this.name,
                variety_id: this.variety.id,
                district_id: this.district.id,
                year_planted: this.year_planted,
                area: this.area,
                num_trees: this.num_trees,
                row_spacing: this.row_spacing,
                tree_spacing: this.tree_spacing,
                variety: {
                  __typename: 'Variety',
                  id: this.variety.id,
                  name: this.variety.name,
                },
                cropType: {
                  __typename: 'CropType',
                  id: this.cropType.id,
                  name: this.cropType.name,
                  blocks_count: this.cropType.blocks_count + 1,
                },
              },
            },
          })
          .then(() => {
            this.name = null;
            this.variety = null;
            this.district = null;
            this.year_planted = null;
            this.area = null;
            this.num_trees = null;
            this.row_spacing = null;
            this.tree_spacing = null;
            this.$validator.reset();
            this.$notifySuccess('Block created!');
          })
          .catch(err => {
            this.$mapGraphQLError(err);
          })
          .catch(err => {
            console.error(err);
            this.$notifyFailure(
              'Unfortunately, we were unable to do that!',
            );
          });
      });
    },
  },
};
</script>
